import React, { useState, useEffect, useRef } from "react";
import Lenis from "lenis";
import { Routes, Route, useLocation } from "react-router-dom";
import Navbar from "./components/Navbar";
import About from "./components/About";
import Tournaments from "./components/Tournaments";
import TournamentDetail from "./components/TournamentDetail";
import Login from "./components/Login";
import Logs from "./components/Logs";
import AdminDashboard from "./components/AdminDashboard";
import AuthCallback from "./components/AuthCallback";
import FightClub from "./components/FightClub";
import ZombsRoyale from "./components/ZombsRoyale";
import Cursor from "./components/Cursor";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import "./App.css";

const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;

function getStoredUser() {
  try {
    const raw = localStorage.getItem("zft_user");
    if (!raw) return null;
    const { user, loginTime } = JSON.parse(raw);
    if (Date.now() - loginTime > THIRTY_DAYS) {
      localStorage.removeItem("zft_user");
      return null;
    }
    return user;
  } catch {
    return null;
  }
}

function App() {
  const [user, setUser] = useState(getStoredUser);
  const location = useLocation();

  const lenisRef = useRef(null);

  // Boot Lenis once
  useEffect(() => {
    const lenis = new Lenis({
      lerp: 0.1,
      smoothTouch: false,
      prevent: (node) => node.closest?.(".canvas"),
    });
    lenisRef.current = lenis;

    let rafId;
    function raf(time) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);

    // Auto-resize whenever page content height changes (async data loads, etc.)
    const ro = new ResizeObserver(() => lenis.resize());
    ro.observe(document.body);

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  // On route change: scroll to top then let Lenis recalculate the new
  // page's scroll height after the page has fully painted.
  useEffect(() => {
    const lenis = lenisRef.current;
    if (lenis) {
      lenis.scrollTo(0, { immediate: true });
      const id = setTimeout(() => lenis.resize(), 120);
      return () => clearTimeout(id);
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [location.pathname]);

  return (
    <div className="App">
      {/* Keep Navbar outside transitions to avoid flicker and tone shifts */}
      <Navbar user={user} setUser={setUser} />

      {/* Dark background already sits at body/.App level, so no white between routes */}
      <TransitionGroup className="transition-group">
        {/* Use pathname as key so a route change = one clean fade */}
        <CSSTransition key={location.pathname} timeout={260} classNames="fade" unmountOnExit>
          <div className="route-viewport">
            <Routes location={location}>
              <Route path="/" element={<About />} />
              <Route path="/fight-club" element={<FightClub />} />
              <Route path="/zombs-royale" element={<ZombsRoyale user={user} />} />
              <Route path="/tournaments" element={<Tournaments user={user} />} />
              <Route path="/tournaments/:id" element={<TournamentDetail user={user} />} />
              <Route path="/login" element={<Login setUser={setUser} />} />
              <Route path="/auth/callback" element={<AuthCallback setUser={setUser} />} />
              <Route path="/logs" element={<Logs user={user} />} />
              <Route path="/admin" element={<AdminDashboard user={user} />} />
            </Routes>
          </div>
        </CSSTransition>
      </TransitionGroup>
      {/* Cursor last so its fixed position always renders above backdrop-filter stacking contexts */}
      <Cursor />
    </div>
  );
}

export default App;
